import React, { useState, useEffect } from "react";
import { Tooltip, Whisper } from "rsuite";
import styled from "styled-components";
import "rsuite/dist/rsuite.min.css";
import FileDownloadIcon from "@rsuite/icons/FileDownload";
import { useAuth0 } from "@auth0/auth0-react";
import axiosInstance from "../../../../appHelpers/axiosInstance";
import { SpacedGlassCard, Button } from "../../../ui";

export const FileHeader = ({ recent_files }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [downloading, setDownloading] = useState({});

  const getRecentFile = async (system_id, file_name) => {
    const downloadKey = `${system_id}_${file_name}`;
    setDownloading((prev) => ({ ...prev, [downloadKey]: true }));

    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const response = await axiosInstance({
        url: `/file/download?sme=${system_id}&file_name=${encodeURIComponent(file_name)}`,
        method: "GET",
        responseType: "blob",
        headers: { authorization: `Bearer ${token}` },
      });

      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${system_id}_${file_name}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("File download failed:", error);
    } finally {
      setDownloading((prev) => ({ ...prev, [downloadKey]: false }));
    }
  };

  const formatFileSize = (fileName) => {
    // Extract file extension for display
    const extension = fileName.split(".").pop()?.toUpperCase();
    return extension || "";
  };

  const formatFileName = (fileName) => {
    // Truncate long file names for better display
    if (fileName.length > 25) {
      const extension = fileName.split(".").pop();
      const name = fileName.substring(0, fileName.lastIndexOf("."));
      const truncatedName = name.substring(0, 20) + "...";
      return `${truncatedName}.${extension}`;
    }
    return fileName;
  };

  useEffect(() => {
    // Reset downloading state when files change
    setDownloading({});
  }, [recent_files]);

  if (!recent_files?.files || recent_files.files.length === 0) {
    return null;
  }

  return (
    <FileHeaderContainer>
      <SpacedGlassCard>
        <HeaderContent>
          <HeaderTitle>Recent Files</HeaderTitle>

          <FilesGrid fileCount={recent_files.files.length}>
            {recent_files.files.map((file) => {
              const downloadKey = `${recent_files.system_id}_${file}`;
              const isDownloading = downloading[downloadKey];

              return (
                <Whisper
                  key={file}
                  trigger="hover"
                  speaker={<Tooltip>Download {file}</Tooltip>}
                >
                  <FileCard>
                    <FileInfo>
                      <FileName>{formatFileName(file)}</FileName>
                      {formatFileSize(file) && (
                        <FileType>{formatFileSize(file)}</FileType>
                      )}
                    </FileInfo>

                    <DownloadButton
                      variant="outline"
                      size="small"
                      loading={isDownloading}
                      onClick={() =>
                        getRecentFile(recent_files.system_id, file)
                      }
                    >
                      {isDownloading ? "Downloading..." : "Download"}
                    </DownloadButton>
                  </FileCard>
                </Whisper>
              );
            })}
          </FilesGrid>
        </HeaderContent>
      </SpacedGlassCard>
    </FileHeaderContainer>
  );
};

// Styled Components
const FileHeaderContainer = styled.div`
  width: 100%;
  margin: 1rem auto 1rem auto;
`;

const HeaderContent = styled.div`
  padding: 1rem 1.5rem;
`;

const HeaderTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 1rem 0;
  color: rgba(0, 0, 0, 0.8);
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const FilesGrid = styled.div`
  display: grid;
  grid-template-columns: ${({ fileCount }) =>
    fileCount === 1
      ? "1fr"
      : fileCount === 2
        ? "repeat(2, 1fr)"
        : "repeat(3, 1fr)"};
  gap: 8px;

  @media (max-width: 1200px) {
    grid-template-columns: ${({ fileCount }) =>
      fileCount === 1 ? "1fr" : fileCount === 2 ? "repeat(2, 1fr)" : "repeat(3, 1fr)"};
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 5px;
  }
`;

const FileCard = styled.div`
  display: flex;
  align-items: center;
  gap: 0px;
  padding: 8px 15%;
  background: rgba(255, 255, 255, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  min-height: 60px;

  &:hover {
    background: rgba(255, 255, 255, 0.35);
    border-color: rgba(255, 255, 255, 0.25);
    transform: translateY(-1px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  }
`;

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  flex: 1;
  min-width: 0;
`;

const FileName = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.9);
  line-height: 1.2;
  word-break: break-word;
`;

const FileType = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.3px;
  background: rgba(0, 0, 0, 0.05);
  padding: 2px 6px;
  border-radius: 4px;
  white-space: nowrap;
`;

const DownloadButton = styled(Button)`
  flex-shrink: 0;
  font-size: 12px;
  padding: 6px 12px;
  min-width: 70px;
  margin-left: auto;
`;
